import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, X, Users, Mail, Pencil, Stethoscope } from 'lucide-react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { isValidEmail } from '@/lib/validators';
import { ClinicOnboardingData } from '../types';

// Validation schema for practitioner invitation
const practitionerInviteSchema = z.object({
  email: z
    .string({ required_error: 'Email is required' })
    .min(1, 'Email is required')
    .transform(val => val.trim().toLowerCase())
    .refine(val => val.length > 0, {
      message: 'Email cannot be empty or contain only spaces',
    })
    .refine(isValidEmail, {
      message: 'Please enter a valid email address with a valid domain (e.g., john@example.com)',
    }),
  first_name: z
    .string({ required_error: 'First name is required' })
    .min(1, 'First name is required')
    .transform(val => val.trim())
    .refine(val => val.length >= 2, {
      message: 'First name must be at least 2 characters',
    })
    .refine(val => val.length <= 50, {
      message: 'First name must be at most 50 characters',
    })
    .refine(val => /^[a-zA-Z\s'-]+$/.test(val), {
      message: 'First name can only contain letters, spaces, hyphens, and apostrophes',
    })
    .refine(val => !val.startsWith(' ') && !val.endsWith(' '), {
      message: 'First name cannot start or end with a space',
    }),
  last_name: z
    .string({ required_error: 'Last name is required' })
    .min(1, 'Last name is required')
    .transform(val => val.trim())
    .refine(val => val.length >= 2, {
      message: 'Last name must be at least 2 characters',
    })
    .refine(val => val.length <= 50, {
      message: 'Last name must be at most 50 characters',
    })
    .refine(val => /^[a-zA-Z\s'-]+$/.test(val), {
      message: 'Last name can only contain letters, spaces, hyphens, and apostrophes',
    })
    .refine(val => !val.startsWith(' ') && !val.endsWith(' '), {
      message: 'Last name cannot start or end with a space',
    }),
});

type PractitionerInviteFormData = z.infer<typeof practitionerInviteSchema>;

interface InvitePractitionersStepProps {
  data: ClinicOnboardingData;
  updateData: (updates: Partial<ClinicOnboardingData>) => void;
}

export const InvitePractitionersStep: React.FC<InvitePractitionersStepProps> = ({
  data,
  updateData,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const form = useForm<PractitionerInviteFormData>({
    resolver: zodResolver(practitionerInviteSchema),
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
    },
    mode: 'onChange',
  });

  const addPractitioner = (formData: PractitionerInviteFormData) => {
    // Normalize the data
    const normalizedData = {
      email: formData.email.trim().toLowerCase(),
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
    };

    // If editing, update the existing practitioner
    if (editingIndex !== null) {
      // Check for duplicate email (excluding the current practitioner being edited)
      const isDuplicate = data.practitionersToInvite.some(
        (p, idx) =>
          idx !== editingIndex && p.email.toLowerCase() === normalizedData.email.toLowerCase()
      );

      if (isDuplicate) {
        form.setError('email', {
          type: 'manual',
          message: 'This email has already been added',
        });
        return;
      }

      // Update the practitioner at the editing index
      const updatedPractitioners = [...data.practitionersToInvite];
      updatedPractitioners[editingIndex] = normalizedData;
      updateData({
        practitionersToInvite: updatedPractitioners,
      });
      setEditingIndex(null);
      form.reset();
      return;
    }

    // Check for duplicate email when adding new
    const isDuplicate = data.practitionersToInvite.some(
      p => p.email.toLowerCase() === normalizedData.email.toLowerCase()
    );

    if (isDuplicate) {
      form.setError('email', {
        type: 'manual',
        message: 'This email has already been added',
      });
      return;
    }

    updateData({
      practitionersToInvite: [...data.practitionersToInvite, normalizedData],
    });
    form.reset();
  };

  const editPractitioner = (index: number) => {
    const practitioner = data.practitionersToInvite[index];
    form.setValue('email', practitioner.email);
    form.setValue('first_name', practitioner.first_name);
    form.setValue('last_name', practitioner.last_name);
    setEditingIndex(index);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    form.reset();
  };

  const removePractitioner = (index: number) => {
    const updated = data.practitionersToInvite.filter((_, i) => i !== index);
    updateData({ practitionersToInvite: updated });
  };

  const inputClassName =
    'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus-visible:ring-brand-teal focus-visible:border-brand-teal';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-2">
          Invite Practitioners
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed max-w-xl">
          Add practitioners to invite to this clinic. They will receive an email invitation after
          the clinic is created. This step is optional.
        </p>
      </div>

      {/* Add new practitioner form */}
      <Card className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <CardContent className="p-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(addPractitioner)} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 text-sm font-medium">
                        First Name <span className="text-red-500" style={{ color: '#ef4444' }}>*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="John"
                          className={inputClassName}
                          {...field}
                          onChange={e => {
                            const value = e.target.value || '';
                            field.onChange(value.trimStart());
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 text-sm font-medium">
                        Last Name <span className="text-red-500" style={{ color: '#ef4444' }}>*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Doe"
                          className={inputClassName}
                          {...field}
                          onChange={e => {
                            const value = e.target.value || '';
                            field.onChange(value.trimStart());
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 text-sm font-medium">
                        Email <span className="text-red-500" style={{ color: '#ef4444' }}>*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="john@example.com"
                          className={inputClassName}
                          {...field}
                          onChange={e => {
                            const value = e.target.value || '';
                            field.onChange(value.toLowerCase().trimStart());
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  type="submit"
                  size="sm"
                  className="gap-2 bg-brand-teal text-white hover:bg-brand-teal/90 border-0 shadow-sm"
                >
                  {editingIndex !== null ? (
                    <>
                      <Pencil className="h-4 w-4" />
                      Update Practitioner
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Add Practitioner
                    </>
                  )}
                </Button>

                {editingIndex !== null && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={cancelEdit}
                    className="gap-2 border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Owner registered as practitioner */}
      {data.add_as_practitioner && data.admin_first_name && data.admin_last_name && (
        <Card className="rounded-xl border border-teal-200 bg-teal-50/40 shadow-sm">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                <Stethoscope className="h-4 w-4 text-teal-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">
                  {data.admin_first_name} {data.admin_last_name}
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {data.admin_email}
                  <span className="ml-1 text-teal-600 font-medium">· Owner</span>
                  {data.practitioner_npi && <span className="text-gray-400">· NPI: {data.practitioner_npi}</span>}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List of added practitioners */}
      {data.practitionersToInvite.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-700">
            Practitioners to Invite ({data.practitionersToInvite.length})
          </h4>
          <div className="space-y-2">
            {data.practitionersToInvite.map((practitioner, index) => (
              <Card
                key={index}
                className={`rounded-xl border border-gray-200 bg-white shadow-sm ${
                  editingIndex === index ? 'ring-2 ring-brand-teal border-brand-teal' : ''
                }`}
              >
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-teal/10 flex items-center justify-center">
                      <Users className="h-4 w-4 text-brand-teal" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">
                        {practitioner.first_name} {practitioner.last_name}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {practitioner.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => editPractitioner(index)}
                      className="h-8 px-3 text-brand-teal border-brand-teal/20 hover:bg-brand-teal/10 hover:text-brand-teal"
                      disabled={editingIndex !== null}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removePractitioner(index)}
                      className="h-8 px-3 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-600"
                      disabled={editingIndex !== null}
                    >
                      <X className="h-3.5 w-3.5 mr-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        !(data.add_as_practitioner && data.admin_first_name) && (
          <Card className="border-dashed border-gray-300 bg-white rounded-xl">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3 ring-1 ring-gray-200">
                <Users className="h-6 w-6 text-gray-400" />
              </div>
              <h4 className="font-semibold text-gray-700 mb-1">No practitioners added yet</h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Add practitioners above or skip this step. You can always invite practitioners later.
              </p>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
};

export default InvitePractitionersStep;
